import { PayIdIcon } from "@picklebet/ui";
import type {
  DepositMethodDescriptor,
  DestinationMethodDescriptor,
  SelectableMethodDescriptor,
} from "../types";
import React from "react";
import { DebitCardPicker } from "../components/deposit-wizard/debit-card-picker/DebitCardPicker";
import { AddDebitCardStep } from "../components/deposit-wizard/steps/add-debit-card/AddDebitCardStep";
import { SelectDebitCardStep } from "../components/deposit-wizard/steps/select-debit-card/SelectDebitCardStep";
import { MethodIcon } from "../components/deposit-wizard/method-row/MethodIcon";
import { CardMethodIcon } from "../components/deposit-wizard/card-icon/CardMethodIcon";
import {
  DebitCardPrimaryView,
  DebitCardPrimaryViewSkeleton,
} from "../components/deposit-wizard/primary-views/debit-card/DebitCardPrimaryView";
import { PayIdStep } from "../components/deposit-wizard/steps/payid/PayIdStep";
import {
  GooglePayPrimaryView,
  GooglePayPrimaryViewSkeleton,
} from "../components/deposit-wizard/primary-views/google-pay/GooglePayPrimaryView";
import {
  ApplePayPrimaryView,
  ApplePayPrimaryViewSkeleton,
} from "../components/deposit-wizard/primary-views/apple-pay/ApplePayPrimaryView";
import { ReactComponent as GooglePayIcon } from "assets/logos/google-pay.svg";
import { ReactComponent as ApplePayIcon } from "assets/logos/apple-pay.svg";
import { InteracMethodIcon } from "../components/deposit-wizard/method-row/InteracMethodIcon";
import {
  InteracPrimaryView,
  InteracPrimaryViewSkeleton,
} from "../components/deposit-wizard/primary-views/interac/InteracPrimaryView";
import { SkrillMethodIcon } from "../components/deposit-wizard/method-row/SkrillMethodIcon";
import {
  SkrillPrimaryView,
  SkrillPrimaryViewSkeleton,
} from "../components/deposit-wizard/primary-views/skrill/SkrillPrimaryView";
import { SkrillStatusStep } from "../components/deposit-wizard/steps/skrill-status/SkrillStatusStep";

const googlePay = {
  id: "google-pay",
  label: "Google Pay",
  Icon: () => (
    <MethodIcon>
      <GooglePayIcon />
    </MethodIcon>
  ),
  primary: {
    title: "Enter Deposit Amount",
    Component: GooglePayPrimaryView,
    Skeleton: GooglePayPrimaryViewSkeleton,
  },
} as const satisfies SelectableMethodDescriptor;

const applePay = {
  id: "apple-pay",
  label: "Apple Pay",
  Icon: () => (
    <MethodIcon>
      <ApplePayIcon />
    </MethodIcon>
  ),
  primary: {
    title: "Enter Deposit Amount",
    Component: ApplePayPrimaryView,
    Skeleton: ApplePayPrimaryViewSkeleton,
  },
} as const satisfies SelectableMethodDescriptor;

const payId = {
  id: "payid",
  label: "PayID",
  Icon: () => (
    <MethodIcon>
      <PayIdIcon />
    </MethodIcon>
  ),
  destination: {
    name: "payid",
    title: "Using PayID",
    Component: PayIdStep,
  },
} as const satisfies DestinationMethodDescriptor;

const debitCard = {
  id: "debit-card",
  label: "Debit Card",
  Icon: CardMethodIcon,
  primary: {
    title: "Enter Deposit Amount",
    Component: DebitCardPrimaryView,
    Skeleton: DebitCardPrimaryViewSkeleton,
  },
  steps: [
    {
      name: "debit-card/select",
      title: "Select Card",
      Component: SelectDebitCardStep,
    },
    {
      name: "debit-card/add",
      title: "Enter Card Details",
      Component: AddDebitCardStep,
    },
  ],
  PickerAction: DebitCardPicker,
} as const satisfies SelectableMethodDescriptor;

const skrill = {
  id: "skrill",
  label: "Skrill",
  Icon: SkrillMethodIcon,
  primary: {
    title: "Enter Deposit Amount",
    Component: SkrillPrimaryView,
    Skeleton: SkrillPrimaryViewSkeleton,
  },
  steps: [
    {
      name: "skrill/status",
      Component: SkrillStatusStep,
      terminal: true,
    },
  ] as const,
} as const satisfies SelectableMethodDescriptor;

const interac = {
  id: "interac",
  label: "Interac",
  Icon: InteracMethodIcon,
  primary: {
    title: "Enter Deposit Amount",
    Component: InteracPrimaryView,
    Skeleton: InteracPrimaryViewSkeleton,
  },
} as const satisfies SelectableMethodDescriptor;

// order is display order on the picker, and the fallback the wizard opens on
// when no wallet is available. Destinations sit among the rest here, but the
// picker renders them after every selectable row regardless. Which method a
// new user opens on is `pickDefaultMethod`, not this order.
export const DEPOSIT_METHODS = [
  debitCard,
  applePay,
  googlePay,
  payId,
  skrill,
  interac,
] as const satisfies readonly DepositMethodDescriptor[];

type DepositMethods = typeof DEPOSIT_METHODS;
type DepositMethodsWithSteps = Extract<
  DepositMethods[number],
  { steps: readonly unknown[] }
>;
type DepositMethodChildStepName =
  DepositMethodsWithSteps["steps"][number]["name"];
type DepositDestinationStepName = Extract<
  DepositMethods[number],
  { destination: unknown }
>["destination"]["name"];
// only names actually registered as a Wizard.Step: method ids are not steps,
// so admitting them here would let goTo() type-check its way to an empty body.
// `deposit-success` is the exception, it isn't registered when the wizard
// closes on success, so report a deposit through useCompleteDeposit not goTo
export type DepositMethodStepName =
  | "deposit"
  | "deposit-success"
  | "deposit-method"
  | DepositDestinationStepName
  | DepositMethodChildStepName;
